import {
    TraceScene,
    readImportArgs,
    type ImportArg,
    type ImportOptions,
    type UnpackReferenceOptions,
    type UnpackReferenceResult,
    type PlaceCopyOptions,
} from "./TraceScene";
import type { SceneEventSystem } from "./SceneEventSystem";
import { ConfigData } from "./configdata";
import type { SafeAreaInsets } from '../scenedata/common';

// Direct imports for optimal tree shaking
import { Engine } from "@babylonjs/core/Engines/engine";
import { Scene } from "@babylonjs/core/scene";
import { PointerInfo, PointerEventTypes } from "@babylonjs/core/Events/pointerEvents";
import { Color4 } from "@babylonjs/core/Maths/math.color";
import { AdvancedDynamicTexture } from "@babylonjs/gui/2D/advancedDynamicTexture";

import {
    SceneExporter,
    type ExportOptions,
    type BundleExportOptions,
    type DocumentExportOptions,
    type FolderExportOptions,
    type SceneFolder,
    type PromoteToAssetOptions,
    type PromotedAsset,
} from "../export/SceneExporter";
import { CustomLoadingScreen } from "../utils/LoadingScreen";
import type { IProgressTracker } from "../utils/ProgressTracker";
import { SceneObjectData } from "../scenedata/sceneobjectdata";
import { Utility } from "../utils/Utility";
import { Logger, LogLevel } from '../utils/Logger';
import { disposedError, throwIfDisposed } from '../utils/abort';
import { ClickToSelect, type ClickToSelectOptions } from "../managers/ClickToSelect";
import { SelectionOutline } from "../managers/SelectionOutline";
import { XRManager, XRSessionState } from "../managers/XRManager";
import { requireLocalizerSession } from "../managers/localizerTracking";
import type { ScenePlacement } from "../cms/localizers";
import { ModelInfo, type SceneInfoResult, type GLBModelInfo } from "../utils/ModelInfo";
import { SceneObject } from "./SceneObject";
import { TraceBehavior } from "../behaviors/tracebehavior";
import { BehaviorData, LoadError } from "../scenedata/behaviordata";
import { SceneCamera } from "./Camera";
import type { CameraFocusOptions } from "./Camera";
import { registerTracePlugins } from "../loaders/plugins/registerPlugins";
import { isGltfExtension } from "../loaders/plugins/sceneFormatExtensions";
import { isSceneDocument, readSceneDocumentJson } from "../loaders/formats/gltf21/normalize";

/** A file already downloaded by {@link TraceViewer.open}'s content sniff. */
interface PrefetchedAsset {
    blob: Blob;
    fileName: string;
    assetBaseUrl: string;
}

/**
 * Result from TraceViewer.import()
 */
export interface LoadResult {
    /** The loaded SceneObject */
    sceneObject: SceneObject;
    /** Successfully loaded behaviors */
    behaviors: TraceBehavior[];
    /** Failed behaviors (non-fatal) */
    errors?: LoadError[];
}

/** Options accepted by {@link TraceViewer.open}. */
export interface OpenOptions {
    /** URL to fetch. */
    src: string;
    /** Behaviors to apply to the loaded content's root node. */
    behaviors?: BehaviorData[];
    /**
     * Optional callback for tracking loading progress. Reports a single
     * normalized 0→1 value spanning the entire load (download + build).
     */
    onProgress?: (progress: number) => void;
    /**
     * When true, load content but skip framing, camera attachment, and the
     * render loop. Call `startRendering()` later to activate. Useful for
     * preloading in a hidden element.
     */
    deferRendering?: boolean;
    /**
     * Revalidate the top-level fetch with the origin rather than serving a
     * cache entry that is merely young enough — see
     * {@link AssetFetchOptions.revalidate}.
     *
     * An editor reopening content it just published wants this: the library
     * reference it opens by is a 302 the browser caches for a minute
     * (spec §2.7), so without it the reopen can show the pre-publish document
     * and the edit looks lost. Applies to THIS fetch only; the files the
     * document names stay cacheable forever, which is what content-addressed
     * names are for.
     */
    revalidate?: boolean;
}

// 2024
// Author: Aaron Post

export class TraceViewer extends HTMLElement implements SceneEventSystem {
    private canvas: HTMLCanvasElement;
    private engine: Engine | null = null;
    public traceScene: TraceScene | null = null;
    private loaded: boolean = false;

    public get scene(): Scene | null {
        return this.traceScene?.getBabylonScene() ?? null;
    }

    // Feature modules
    private clickToSelect: ClickToSelect | null = null;
    private selectionOutline: SelectionOutline | null = null;
    private xrManager: XRManager | null = null;
    private modelInfo: ModelInfo | null = null;
    private _localizers: ScenePlacement[] | null = null;
    private _selectedPlacementId?: string;
    private _imageAlignmentConfig: { imageUrl: string; widthM: number } | null = null;

    /**
     * SceneExporter is stateless; build a fresh one per call so it always
     * targets the current TraceScene (defends against external reassignment
     * of the public `traceScene` field).
     */
    private exporter(): SceneExporter {
        if (!this.traceScene) {
            throw new Error("Scene is not initialized");
        }
        return new SceneExporter(this.traceScene);
    }

    // Buffered edit-mode click-to-select preference, set before traceScene exists
    // (e.g. at session init). Forwarded to TraceScene once it's created.
    private _pendingEditModeClickToSelect: boolean | null = null;
    private _clickToSelectOptions: ClickToSelectOptions = {};

    // Bound event handlers (stored for proper cleanup)
    private _resizeHandler: (() => void) | null = null;

    // Ready state tracking
    private _ready: boolean = false;
    private _readyPromise: Promise<void> | null = null;
    private _readyResolve: (() => void) | null = null;
    private _readyReject: ((reason: Error) => void) | null = null;

    constructor() {
        super();

        this.canvas = document.createElement("canvas");

        this.initializeStyle();
    }

    // Public getter for canvas (needed for inspector integration)
    public getCanvas(): HTMLCanvasElement {
        return this.canvas;
    }

    // Public getter for engine
    public getEngine(): Engine | null {
        return this.engine;
    }

    /**
     * Activate rendering: attach camera controls, render the first frame,
     * and start the render loop. Use after `open({ deferRendering: true })`.
     */
    public startRendering(): void {
        this.traceScene?.startRendering();
    }

    /** Set the scene background (clear) color from a hex string (e.g. '#111111'). */
    public setBackgroundColor(hex: string): void {
        const scene = this.traceScene?.getBabylonScene();
        if (!scene) return;
        const c = Color4.FromHexString(hex.length === 7 ? hex + 'ff' : hex);
        scene.clearColor = c;
    }

    /** Frame the camera to fit the scene content. */
    public frameScene(): void {
        this.traceScene?.frameScene();
    }

    /**
     * Reset the camera to re-center on the scene/object.
     * Useful after panning has offset the camera target.
     * 
     * @param animate - Whether to animate the transition (default: true)
     */
    public resetCamera(animate: boolean = true): void {
        if (!this.traceScene) {
            Logger.log('[TraceViewer] Cannot reset camera - scene not initialized');
            return;
        }

        const camera = this.traceScene.getCamera();
        if (!camera) {
            Logger.log('[TraceViewer] Cannot reset camera - no camera found');
            return;
        }

        SceneCamera.resetCamera(this.traceScene.getBabylonScene(), camera, animate, this.traceScene.safeAreaInsets);
    }

    /**
     * Focus (frame) the camera on a single scene object — the editor "F"
     * hotkey. Animates the camera to fit the object's bounds and re-centers
     * the orbit pivot on it. Objects with no renderable bounds (empties,
     * particle emitters) are centered at their origin.
     *
     * @param object - The scene object to frame
     * @param options - Padding (`radiusScale`, default 1.5) and animation
     *                  duration (`framingTime` ms, default 300)
     */
    public focusObject(object: SceneObject, options?: CameraFocusOptions): void {
        const camera = this.traceScene?.getCamera();
        if (!camera) {
            Logger.log('[TraceViewer] Cannot focus object - no camera found');
            return;
        }

        SceneCamera.focusOnNode(camera, object.transform, options, this.traceScene?.safeAreaInsets);
    }

    /**
     * Focus (frame) the camera on the combined bounds of several scene objects
     * — the editor "F" hotkey with a multi-object selection. Frames and centers
     * the whole selection rather than just one object. Falls back to
     * {@link focusObject} for a single object and does nothing for an empty set.
     *
     * @param objects - The scene objects to frame
     * @param options - Padding (`radiusScale`, default 1.5) and animation
     *                  duration (`framingTime` ms, default 300)
     */
    public focusObjects(objects: SceneObject[], options?: CameraFocusOptions): void {
        if (objects.length === 0) return;
        const camera = this.traceScene?.getCamera();
        if (!camera) {
            Logger.log('[TraceViewer] Cannot focus objects - no camera found');
            return;
        }

        SceneCamera.focusOnNodes(camera, objects.map(o => o.transform), options, this.traceScene?.safeAreaInsets);
    }

    /**
     * Returns a promise that resolves when the viewer is fully loaded and ready.
     * If the viewer is already ready, resolves immediately. Rejects with an
     * `AbortError` once the viewer is disposed, whether it was waiting or is
     * asked afterwards.
     *
     * @example
     * ```typescript
     * await viewer.whenReady();
     * // Viewer is now ready to use
     * const sceneData = viewer.getSceneData();
     * ```
     */
    public whenReady(): Promise<void> {
        if (this._ready) {
            return Promise.resolve();
        }

        if (!this._readyPromise) {
            this._readyPromise = new Promise<void>((resolve, reject) => {
                this._readyResolve = resolve;
                this._readyReject = reject;
            });
        }

        return this._readyPromise;
    }

    /**
     * Check if the viewer is fully loaded and ready.
     */
    public get isReady(): boolean {
        return this._ready;
    }

    private initializeStyle() {
        // Only add the global styles once
        if (!document.querySelector('style[data-trace-viewer-global]')) {
            const style = document.createElement("style");
            style.setAttribute('data-trace-viewer-global', 'true');
            style.textContent = `
                trace-viewer {
                    display: block;
                    width: 100%;
                    height: 100%;
                    position: relative;
                    user-select: none;
                    -webkit-user-select: none;
                }
                trace-viewer canvas {
                    width: 100%;
                    height: 100%;
                    outline: none;
                    cursor: grab;
                    touch-action: none;
                }
            `;
            document.head.appendChild(style);
        }
    }

    /**
     * Extensions handled by `traceScene.loadFromBlob` (the Trace scene-format
     * loader pipeline registered in {@link ../loaders/plugins/registerPlugins}).
     * Mix of zip archives (`.glz`/`.zip`/`.trace`/`.tracex`/`.real`) and JSON
     * (`.glxf`); the loader routes by extension. `.gltf`/`.glb` are NOT here —
     * they name a plain model as often as a scene document, and route on
     * content (see {@link open}). Anything outside both is a generic
     * single-asset file (GLB, image, video, …) — unless its BYTES turn out to
     * be a bundle, which `TraceScene._loadFromBlob` sniffs for on every
     * archive it is handed (spec §4).
     */
    private static readonly TRACE_FORMAT_EXTENSIONS = ['glz', 'zip', 'trace', 'tracex', 'glxf', 'real'];

    /** Share of the progress bar the pre-dispatch download takes ({@link _openByContent}). */
    private static readonly SNIFF_DOWNLOAD_WEIGHT = 0.1;

    /**
     * Open a file by URL, clearing any existing scene. Dispatches between two
     * internal loaders:
     *
     *   - Trace scene formats (`.glz`/`.zip`/`.trace`/`.tracex`/`.glxf`/`.real`) →
     *     `traceScene.loadFromBlob`. The format is opinionated about its own
     *     scene-root behaviors, so `options.behaviors` is treated as additive.
     *   - `.gltf`/`.glb`, and a URL with NO extension at all → whichever of the
     *     two the **content** says: a document (`externalAssets` or
     *     `extras.trace`) takes the scene path, anything else is a single asset
     *     (migration plan D2 — route on content, never on the extension).
     *   - Anything else (an image, a video, …) → `traceScene.openAsRoot`.
     *
     * For loading a CMS Scene (RootContent + image anchor + ReferenceContent
     * orchestration), see `loadCmsScene` in `trace-viewer/cms`.
     *
     * Rejects with an `AbortError` if the viewer is disposed while loading.
     */
    public async open(options: OpenOptions): Promise<void> {
        const ext = Utility.getFileExtensionFromSrc(options.src);
        if (TraceViewer.TRACE_FORMAT_EXTENSIONS.includes(ext)) {
            return this._loadAsTraceFormat(options);
        }
        // `.gltf`/`.glb` name a document as often as a plain model, and an
        // extension-less URL names nothing at all — a library reference is
        // `<api>/assets/{id}/file` (spec §3.4), which is how an editor opens
        // every library row. Both are answered by the bytes (issue #849).
        if (isGltfExtension(ext) || !ext) {
            return this._openByContent(options);
        }
        return this._loadAsSingleAsset(options);
    }

    /**
     * Open a URL whose extension cannot classify it: download once, classify by
     * the RESOLVED file name plus a content sniff, then dispatch.
     *
     * Two URLs land here. `.gltf`/`.glb` names both a scene document and a
     * plain model. An extension-less URL — the library reference
     * `<api>/assets/{id}/file` — names neither: it 302s to the asset's folder,
     * and only the name it lands on (`scene.<hash>.gltf`, `chair.<hash>.glb`,
     * an image) says what arrived (spec §3.4).
     *
     * The two paths differ in more than the loader: `options.behaviors` — the
     * externally-stored root behaviors of a legacy raw-leaf CMS row — apply to
     * a **single asset only**. A document carries its own root behaviors in
     * `scenes[n].extras.trace` and never consults a row (spec §3.6, §5); it
     * must also reach `loadFromBlob`, the only path that applies them to the
     * implicit scene root, rather than `openAsRoot`, which would drop them and
     * stamp the document's own root with the URL of the document it belongs to
     * (issue #849).
     */
    private async _openByContent(options: OpenOptions): Promise<void> {
        if (!this.traceScene) {
            throw new Error('Scene not initialized');
        }

        const weight = TraceViewer.SNIFF_DOWNLOAD_WEIGHT;
        const prefetched = await this.traceScene.fetchAssetWithProgress(
            options.src,
            (loaded, total) => {
                options.onProgress?.(weight * (total > 0 ? Math.min(1, loaded / total) : 0));
            },
            { revalidate: !!options.revalidate },
        ).catch((error: unknown) => {
            // The only download here without a loader's catch: a failure after the dispose is the teardown.
            throwIfDisposed(this);
            throw error;
        });

        // What the fetch RESOLVED to classifies first: a reference can land on
        // any of the formats `open` dispatches by extension above, and one that
        // names itself unambiguously needs no sniff. `options.behaviors` stay
        // additive there, exactly as for a URL that named the format itself.
        const resolvedExtension = Utility.getFileExtensionFromSrc(prefetched.fileName);
        if (TraceViewer.TRACE_FORMAT_EXTENSIONS.includes(resolvedExtension)) {
            return this._loadAsTraceFormat(options, prefetched);
        }

        // The sniff is for the glTF family only — and for a name that still
        // says nothing, where it is the last thing that can. An image or a
        // video resolved by name is not read.
        const json = isGltfExtension(resolvedExtension) || !resolvedExtension
            ? await readSceneDocumentJson(prefetched.blob)
            : undefined;
        throwIfDisposed(this);
        if (!json || !isSceneDocument(json)) {
            return this._loadAsSingleAsset(options, prefetched);
        }

        if (options.behaviors?.length) {
            Logger.warn(
                `[TraceViewer] Ignoring ${options.behaviors.length} externally-stored behaviors for ` +
                `${options.src}: a scene document carries its own root behaviors.`
            );
        }
        return this._loadAsTraceFormat({ ...options, behaviors: undefined }, prefetched);
    }

    /**
     * Load via the Trace scene-format loader pipeline (`loadFromBlob`). Used
     * by {@link open} for `.glz`/`.zip`/`.trace`/`.tracex`/`.glxf`/`.real`.
     */
    private async _loadAsTraceFormat(options: OpenOptions, prefetched?: PrefetchedAsset): Promise<void> {
        if (!this.traceScene || !this.engine) {
            throw new Error('Scene not initialized');
        }

        const loadingScreen = this._beginLoad();

        try {
            // Trace formats: small descriptor (zip or JSON) + heavy referenced
            // assets. Download is fast; building (behavior loading) is the
            // slow part. Weight accordingly.
            const DOWNLOAD_WEIGHT = 0.1;

            this.traceScene.clearScene();

            const onDownload = loadingScreen.trackDownload(DOWNLOAD_WEIGHT);
            const { blob, fileName, assetBaseUrl } = prefetched ?? await this.traceScene.fetchAssetWithProgress(
                options.src,
                (loaded, total) => {
                    onDownload(loaded, total);
                    // loaded can overshoot total (compressed transfers) — clamp
                    options.onProgress?.(DOWNLOAD_WEIGHT * (total > 0 ? Math.min(1, loaded / total) : 0));
                },
                { revalidate: !!options.revalidate },
            );

            // Byte-level download progress fires once per network chunk —
            // thousands of times for a large scene. Only log whole-percent
            // changes: with DevTools open, per-chunk console output makes
            // loading many times slower than the download itself.
            let lastLoggedPercent = -1;
            const progressTracker: IProgressTracker = {
                onStart: (total: number) => {
                    loadingScreen.onStart(total);
                    Logger.log(`[TraceViewer] progress onStart: total=${total}`);
                    options.onProgress?.(DOWNLOAD_WEIGHT);
                },
                onProgress: (loaded: number, total: number) => {
                    loadingScreen.onProgress(loaded, total);
                    const buildProgress = total > 0 ? loaded / total : 0;
                    const overall = DOWNLOAD_WEIGHT + (1 - DOWNLOAD_WEIGHT) * buildProgress;
                    const percent = Math.floor(overall * 100);
                    if (percent !== lastLoggedPercent) {
                        lastLoggedPercent = percent;
                        Logger.log(`[TraceViewer] progress: ${loaded.toFixed(2)}/${total} → ${percent}%`);
                    }
                    options.onProgress?.(overall);
                },
                onComplete: () => {
                    loadingScreen.onComplete();
                    Logger.log(`[TraceViewer] progress onComplete`);
                }
            };

            await this.traceScene.loadFromBlob(blob, fileName, progressTracker, options.behaviors, assetBaseUrl);

            this._finishLoad(options.deferRendering ?? false);
            Logger.log(`[TraceViewer] Opened trace-format: ${options.src} (${options.behaviors?.length ?? 0} extra behaviors)`);
        } catch (error) {
            this._loadFailed(error, options.src);
        } finally {
            this.engine?.hideLoadingUI();
        }
    }

    /**
     * Load a single asset (GLB, image, video, …) as the scene root. Used by
     * {@link open} for non-Trace-format files.
     */
    private async _loadAsSingleAsset(options: OpenOptions, prefetched?: PrefetchedAsset): Promise<void> {
        if (!this.traceScene || !this.engine) {
            throw new Error('Scene not initialized');
        }

        const loadingScreen = this._beginLoad();

        try {
            // Single assets: download IS the slow part. Allocate 90% download, 10% build.
            const DL_WEIGHT = 0.9;

            const buildTracker: IProgressTracker = {
                onStart: (total: number) => {
                    loadingScreen.onStart(total);
                    options.onProgress?.(DL_WEIGHT);
                },
                onProgress: (loaded: number, total: number) => {
                    loadingScreen.onProgress(loaded, total);
                    options.onProgress?.(DL_WEIGHT + (1 - DL_WEIGHT) * (total > 0 ? loaded / total : 0));
                },
                onComplete: () => { loadingScreen.onComplete(); }
            };

            const onDownload = loadingScreen.trackDownload(DL_WEIGHT);
            await this.traceScene.openAsRoot(
                options.src,
                options.behaviors,
                (loaded, total) => {
                    onDownload(loaded, total);
                    // loaded can overshoot total (compressed transfers) — clamp
                    options.onProgress?.(DL_WEIGHT * (total > 0 ? Math.min(1, loaded / total) : 0));
                },
                buildTracker,
                prefetched,
                { revalidate: !!options.revalidate },
            );

            this._finishLoad(options.deferRendering ?? false);
            Logger.log(`[TraceViewer] Opened asset: ${options.src} (${options.behaviors?.length ?? 0} extra behaviors)`);
        } catch (error) {
            this._loadFailed(error, options.src);
        } finally {
            this.engine?.hideLoadingUI();
        }
    }

    /** A load failed: a dispose that coincided with it is a teardown to abort with, not an error to log. */
    private _loadFailed(error: unknown, what: string): never {
        throwIfDisposed(this);
        Logger.error(`[TraceViewer] Error opening ${what}: ${String(error)}`);
        throw error;
    }

    /** Install the built-in loading overlay and start displaying it. */
    private _beginLoad(): CustomLoadingScreen {
        const loadingScreen = new CustomLoadingScreen("Loading...", this);
        this.engine!.loadingScreen = loadingScreen;
        this.engine!.displayLoadingUI();
        return loadingScreen;
    }

    /** Reset feature modules after clear, then frame/start unless deferred. */
    private _finishLoad(deferRendering: boolean): void {
        throwIfDisposed(this);
        this.resetAfterClear();
        if (!deferRendering) {
            this.traceScene!.frameScene();
            this.traceScene!.startRendering();
        }
    }

    /**
     * Open an asset from a Blob, replacing any current content.
     * Shows a loading screen with progress while loading.
     *
     * @param blob - The scene blob data
     * @param fileName - The file name with extension (e.g., "scene.trace") - determines how the file is processed
     * @param options.siblings - Companion files accompanying the opened file
     *   (a .gltf's .bin/textures, a .glxf's referenced GLBs), keyed by the
     *   relative path the file references them by
     *
     * @example
     * ```typescript
     * const response = await fetch('scene.trace');
     * const blob = await response.blob();
     * await viewer.openBlob(blob, 'scene.trace');
     * ```
     */
    public async openBlob(
        blob: Blob,
        fileName: string,
        options?: { siblings?: ReadonlyMap<string, Blob> }
    ): Promise<void> {
        if (!this.traceScene || !this.engine) {
            throw new Error('Scene not initialized');
        }

        // Show loading screen
        const loadingScreen = new CustomLoadingScreen("Loading...", this);
        this.engine.loadingScreen = loadingScreen;
        this.engine.displayLoadingUI();

        try {
            this.traceScene.clearScene();
            // Register AFTER the clear (clearScene wipes ResourceManager assets).
            if (options?.siblings?.size) {
                this.traceScene.resources.registerSiblingAssets(options.siblings);
            }
            await this.traceScene.loadFromBlob(blob, fileName, loadingScreen);

            this._finishLoad(false);
            Logger.log(`[TraceViewer] Opened blob: ${fileName}`);
        } catch (error) {
            this._loadFailed(error, `blob ${fileName}`);
        } finally {
            this.engine?.hideLoadingUI();
        }
    }

    /**
     * Reset feature modules after clearScene().
     * clearScene() disposes effect layers even though the Scene object is reused,
     * so the outline layer needs to be recreated. Also triggers a resize to fix
     * camera aspect ratio.
     */
    private resetAfterClear(): void {
        this.selectionOutline?.resetOutlineLayer();

        // Trigger resize to ensure camera aspect ratio is correct
        this.engine?.resize();
    }

    /**
     * Clear the current scene content and reset to an empty state.
     * The Babylon Scene object is preserved for reuse.
     */
    public clearScene(): void {
        if (!this.traceScene) return;

        this.traceScene.clearScene();
        this.resetAfterClear();
    }

    /**
     * Import an asset from a URL into the existing scene (additive).
     * Use this to add additional objects to an already-open scene.
     *
     * For opening a new scene, use `open()` instead.
     *
     * @param url - URL to the asset (GLB, JPG, PNG, MP4, .trace)
     * @param name - Optional custom name for the scene object (defaults to filename)
     * @param behaviors - Optional behaviors to apply to the loaded object after import
     * @param options - Optional import options (see {@link ImportOptions})
     * @returns LoadResult with sceneObject and its behaviors
     *
     * @example
     * ```typescript
     * const { sceneObject } = await viewer.import('extra-model.glb', 'Prop');
     * ```
     */
    public async import(url: string, name?: string, behaviors?: BehaviorData[], options?: ImportOptions): Promise<LoadResult>;

    /**
     * Import an asset from a Blob into the existing scene (additive).
     *
     * @param blob - The asset blob (File or Blob)
     * @param fileName - The file name with extension (e.g., "model.glb") - determines asset type
     * @param name - Optional custom name for the scene object (defaults to fileName without extension)
     * @param behaviors - Optional behaviors to apply to the loaded object after import
     * @param options - Optional import options (see {@link ImportOptions})
     * @returns LoadResult with sceneObject and its behaviors
     *
     * @example
     * ```typescript
     * const { sceneObject } = await viewer.import(file, file.name);
     * // place a .trace component as editable objects of this scene
     * const placed = await viewer.import(file, 'sofa.trace', 'Sofa', undefined, { unpack: true });
     * ```
     */
    public async import(blob: Blob, fileName: string, name?: string, behaviors?: BehaviorData[], options?: ImportOptions): Promise<LoadResult>;

    // Implementation. The optional tail is read by shape
    // ({@link readImportArgs}), never by position.
    public async import(
        urlOrBlob: string | Blob,
        fileNameOrName?: string,
        ...rest: ImportArg[]
    ): Promise<LoadResult> {
        if (!this.traceScene) {
            throw new Error('Scene not initialized');
        }

        let sceneObject: SceneObject | null;

        if (typeof urlOrBlob === 'string') {
            // URL overload: import(url, name?, behaviors?, options?)
            const { behaviors, options } = readImportArgs(rest);
            sceneObject = await this.traceScene.loadAsset(urlOrBlob, fileNameOrName, behaviors, options);
        } else {
            // Blob overload: import(blob, fileName, name?, behaviors?, options?)
            if (!fileNameOrName) {
                throw new Error('fileName is required when importing from Blob');
            }
            const { name, behaviors, options } = readImportArgs(rest);
            sceneObject = await this.traceScene.loadAsset(urlOrBlob, fileNameOrName, name, behaviors, options);
        }

        if (!sceneObject) {
            throw new Error('No objects loaded');
        }

        return {
            sceneObject,
            behaviors: sceneObject.getBehaviors(),
            errors: undefined
        };
    }

    /**
     * Dissolve a placed reference in place, so its content becomes this
     * scene's own — issue #730, spec §2.6 / §3.4.
     *
     * The explicit inverse of placing a library asset or a `.trace`: the
     * stand-in node goes, the content stays where it is, and
     * `Provenance.placedFrom` records the URL the reference carried. Pass
     * `intoFolder` to bring the referenced folder's own files across at the
     * same time — that is "Copy to scene", and it is the one gesture that
     * freezes a live reference.
     *
     * See {@link TraceScene.unpackReference} for the full contract.
     */
    public async unpack(
        reference: SceneObject,
        options: UnpackReferenceOptions = {},
    ): Promise<UnpackReferenceResult> {
        if (!this.traceScene) {
            throw new Error('Scene not initialized');
        }
        return this.traceScene.unpackReference(reference, options);
    }

    /**
     * Place a copy of a library asset under `parent`: its nodes become this
     * scene's own, `Provenance.placedFrom` keeps the link, and references
     * nested in it stay references. Throws, with nothing left behind, when
     * the asset or any piece in it does not load.
     *
     * See {@link TraceScene.placeCopy} for the full contract.
     */
    public async placeCopy(url: string, parent: SceneObject, options: PlaceCopyOptions = {}): Promise<SceneObject> {
        if (!this.traceScene) {
            throw new Error('Scene not initialized');
        }
        return this.traceScene.placeCopy(url, parent, options);
    }

    /**
     * Register companion files for a subsequent additive {@link import} of a
     * file with external references (a .gltf's .bin/textures, a .glxf's
     * referenced GLBs). Keys are the relative paths the imported file
     * references them by. For whole-scene opens, pass `siblings` to
     * {@link openBlob} instead (imports don't clear the scene, so registering
     * up front is safe here).
     */
    public registerExternalAssets(siblings: ReadonlyMap<string, Blob>): void {
        if (!this.traceScene) {
            throw new Error('Scene not initialized');
        }
        this.traceScene.resources.registerSiblingAssets(siblings);
    }

    /**
     * Export the scene to a `.trace` / `.zip` Blob.
     *
     * Format is determined by the `zipFormat` option:
     * - `"auto"` (default) — `.zip` (models.json + info.json) if the scene
     *   was loaded from one, otherwise `.trace` (scene.json).
     * - `"zip"` — always `.zip`.
     * - `"trace"` — always `.trace`.
     */
    public async exportToBlob(options: ExportOptions = {}): Promise<Blob> {
        return this.exporter().toTraceBlob(options);
    }

    /** Export a subtree to a `.trace` / `.zip` Blob. */
    public async exportSubtreeToBlob(
        sceneObject: SceneObject,
        options: ExportOptions = {},
    ): Promise<Blob> {
        return this.exporter().subtreeToTraceBlob(sceneObject, options);
    }

    /**
     * Export the scene as a `.glz` **bundle** Blob (spec §4): `scene.gltf` at
     * the archive root, the folder's files under `assets/`.
     */
    public async exportToGlzBlob(options: BundleExportOptions = {}): Promise<Blob> {
        return this.exporter().toGlzBlob(options);
    }

    /** Export a subtree as a `.glz` bundle Blob (spec §4). */
    public async exportSubtreeToGlzBlob(sceneObject: SceneObject, options: BundleExportOptions = {}): Promise<Blob> {
        return this.exporter().subtreeToGlzBlob(sceneObject, options);
    }

    /**
     * @deprecated Renamed to {@link exportToGlzBlob}. The bundle is `.glz`
     *   now (spec §4); `.tracex` is read-only and retires with its rows
     *   (#816). Identical bytes — only the name the file should be given
     *   changes.
     */
    public async exportToTracexBlob(options: BundleExportOptions = {}): Promise<Blob> {
        return this.exportToGlzBlob(options);
    }

    /** @deprecated Renamed to {@link exportSubtreeToGlzBlob} — see above. */
    public async exportSubtreeToTracexBlob(sceneObject: SceneObject, options: BundleExportOptions = {}): Promise<Blob> {
        return this.exportSubtreeToGlzBlob(sceneObject, options);
    }

    /**
     * Export the scene as a standalone glTF 2.1 JSON Blob (`.gltf`).
     *
     * Unlike `.glz`, there's no bundled `assets/` folder: every reference
     * must resolve to a usable absolute URL. This method validates the scene
     * first — any local path, `builtin:` primitive, or `blob:` URL throws
     * before bytes are emitted.
     */
    public async exportSceneToGltfBlob(options: DocumentExportOptions = {}): Promise<Blob> {
        return this.exporter().toGltfBlob(options);
    }

    /**
     * @deprecated Renamed to {@link exportSceneToGltfBlob} — the document is
     *   glTF 2.1 now, not glXF. Kept for one release so consumers can migrate.
     */
    public async exportSceneToGlxfBlob(options: DocumentExportOptions = {}): Promise<Blob> {
        return this.exportSceneToGltfBlob(options);
    }

    /**
     * Export the scene as a **folder** (CONTENT_MODEL_SPEC §2.1): the glTF 2.1
     * document plus the files it names, keyed by their relative URIs.
     *
     * The unit a folder-mode save and an asset upload both send; `.tracex` is
     * `zip()` of this.
     */
    public async exportSceneFolder(options: FolderExportOptions = {}): Promise<SceneFolder> {
        return this.exporter().exportFolder(options);
    }

    /** Export a subtree as a folder — the "Save as asset" path (spec §3.5). */
    public async exportSubtreeFolder(
        sceneObject: SceneObject,
        options: FolderExportOptions = {},
    ): Promise<SceneFolder> {
        return this.exporter().subtreeToFolder(sceneObject, options);
    }

    /**
     * "Save as asset" — promote a selection into a library asset folder
     * (CONTENT_MODEL_SPEC §3.5, SCENE_ROOT_SPEC §4).
     *
     * Returns the document, the files that travel with it into the new folder,
     * and the manifest entries the asset closure rule refuses. Offenders are
     * reported rather than thrown so an editor can show them; a caller must
     * check the list before uploading, because the server's gate refuses the
     * same entries (spec §2.5).
     *
     * See {@link SceneExporter.promoteToAsset} for the full contract.
     */
    public async promoteSubtreeToAsset(
        subtree: SceneObject | readonly SceneObject[],
        options: PromoteToAssetOptions = {},
    ): Promise<PromotedAsset> {
        return this.exporter().promoteToAsset(subtree, options);
    }

    /** Export a single SceneObject as a `.glb` Blob. */
    public async exportObjectToGlbBlob(sceneObject: SceneObject): Promise<Blob> {
        return this.exporter().objectToGlbBlob(sceneObject);
    }

    // Retrieve a blob of a screenshot of the scene
    async toBlob({ mimeType = "image/png" }: { mimeType?: string } = {}): Promise<Blob> {
        if (!this.traceScene || !this.engine) {
            throw new Error("Scene or engine is not initialized");
        }

        const babylonScene = this.traceScene.getBabylonScene();
        const engine = this.engine;

        return new Promise<Blob>((resolve, reject) => {
            babylonScene.onAfterRenderObservable.addOnce(() => {
                const camera = babylonScene.activeCamera;
                if (!camera) {
                    reject(new Error('No active camera'));
                    return;
                }
                void Utility.captureScreenshot(engine, camera, mimeType).then(resolve);
            });

            babylonScene.render();
        });
    }

    /**
     * The engine's implicit scene root — the runtime MOUNT POINT the open
     * document hangs under (the XR pose goes here).
     *
     * It is not authored, not serialized, and carries no behaviors of its own
     * (SCENE_ROOT_SPEC D8). What the open file says about itself — its root
     * behaviors, its `active`, its pivot — is on the **document root**; use
     * {@link getDocumentRoot} for that.
     *
     * @returns The scene root SceneObject, or null if scene hasn't started
     */
    getSceneRoot(): SceneObject | null {
        return this.traceScene?.getSceneRoot() ?? null;
    }

    /**
     * The **document root** — the one root node of the open document
     * (SCENE_ROOT_SPEC D8), and the object an editor treats as "the scene".
     *
     * @returns the document root, or null when the session has no single root
     *   (an empty scene, or a legacy flat `.zip`).
     *
     * @example
     * ```typescript
     * const root = viewer.getDocumentRoot();
     * if (root) {
     *   const graph = root.getBehavior(BehaviorGraph);
     *   const data = root.getSceneObjectData();
     *   data.behaviors.push({ '@type': 'Properties', ... });
     *   await root.setData(data);
     * }
     * ```
     */
    getDocumentRoot(): SceneObject | null {
        return this.traceScene?.getDocumentRoot() ?? null;
    }

    // Push modified scene data to the trace scene, updating the respective scene object
    // Returns the full behaviors array after the object is refreshed
    async pushSceneData(data: SceneObjectData): Promise<BehaviorData[] | null> {
        return this.traceScene ? await this.traceScene.pushSceneData(data) : null;
    }

    // Retrieve the scene data from the trace scene
    getSceneData(): SceneObjectData[] {
        return this.traceScene ? this.traceScene.getSceneDataList() : [];
    }

    connectedCallback() {
        if (this.engine || this.loaded) return;

        this.loaded = true;
        this.setupCanvas();
        try {
            this.initializeEngine();
        } catch (e) {
            const error = e instanceof Error ? e : new Error('WebGL is not supported');
            Logger.error(`[TraceViewer] Engine initialization failed: ${error.message}`);
            this._failReady(error);
            this.dispatchEvent(new CustomEvent('error', { detail: { error } }));
            return;
        }
        const loading = this.loadScene();
        const scene = this.traceScene; // null only when the scene itself could not be created
        loading.catch((e: unknown) => {
            // A failure once the scene is gone is the dispose cutting the load short.
            if (scene && this.traceScene !== scene) return;
            const error = e instanceof Error ? e : new Error(String(e));
            Logger.error(`[TraceViewer] loadScene failed: ${error.message}`);
            this._failReady(error);
            this.dispatchEvent(new CustomEvent('error', { detail: { error } }));
        });

        // Handle window resize
        this._resizeHandler = () => this.onResize();
        window.addEventListener("resize", this._resizeHandler);

        // Prevent horizontal scroll from triggering browser back/forward navigation (macOS two-finger swipe)
        this.canvas.addEventListener("wheel", (e) => {
            if (Math.abs(e.deltaX) > 0) {
                e.preventDefault();
            }
        }, { passive: false });
    }

    /**
     * Settle `whenReady()` as failed, for callers waiting now and for any that
     * ask later. The first failure stands: a viewer that failed to boot keeps
     * its real error when it is disposed afterwards. A viewer that was ready
     * has a settled promise, which is replaced.
     */
    private _failReady(error: Error): void {
        if (this._readyResolve) {
            this._readyReject?.(error);
        } else if (this._ready || !this._readyPromise) {
            const rejected = Promise.reject(error);
            rejected.catch(() => {}); // suppress unhandled-rejection warning
            this._readyPromise = rejected;
        }
        this._readyResolve = null;
        this._readyReject = null;
    }

    private setupCanvas() {
        this.appendChild(this.canvas);
    }

    private initializeEngine() {
        this.engine = new Engine(this.canvas, true, undefined, true /* adaptToDeviceRatio */);

        // Register the Trace scene loader plugins (.trace, .glz, .tracex, .glxf)
        registerTracePlugins();
    }

    private onResize() {
        this.engine?.resize();
    }

    private async loadScene() {
        if (this.traceScene) return;

        // Initialize TraceScene — it creates and owns the Babylon Scene internally
        const config = this.initializeConfig();
        this.traceScene = new TraceScene({
            engine: this.engine!,
            canvas: this.canvas,
            config: config,
            traceViewer: this
        });

        // Flush edit-mode click-to-select preference buffered before scene creation.
        if (this._pendingEditModeClickToSelect !== null) {
            this.traceScene.setEditModeClickToSelect(this._pendingEditModeClickToSelect);
            // Defer outline + routing activation until after clickToSelect is created below.
        }

        // Initialize feature modules with the Scene created by TraceScene
        const babylonScene = this.traceScene.getBabylonScene();
        this.clickToSelect = new ClickToSelect(babylonScene, this.traceScene);

        // Handle double-tap: reset camera, unless a GUI overlay or an
        // interactive object in play mode was hit.  Registered with
        // insertFirst so it runs before FramingBehavior's own double-tap
        // handler; we always skipNextObservers to take full ownership.
        const traceScene = this.traceScene;
        const viewer = this;
        babylonScene.onPointerObservable.add(
            (pointerInfo, eventState) => {
                if (pointerInfo.type !== PointerEventTypes.POINTERDOUBLETAP) return;

                // Take ownership — prevent FramingBehavior's default zoom
                eventState.skipNextObservers = true;

                // Skip if a GUI overlay handled the pointer event
                for (const tex of babylonScene.textures) {
                    if (tex instanceof AdvancedDynamicTexture && tex._shouldBlockPointer) {
                        return;
                    }
                }

                // In play mode, skip camera reset when hitting an interactive object
                if (traceScene.isPlayMode()) {
                    const pick = pointerInfo.pickInfo;
                    if (pick?.hit && pick.pickedMesh) {
                        // Babylon emits DOUBLETAP instead of PICK for the second
                        // rapid tap (even when it hits a different object). Native
                        // select graphs listen to PICK: deliver that second tap
                        // once, while still suppressing double-tap camera framing.
                        try {
                            babylonScene.onPointerObservable.notifyObservers(
                                new PointerInfo(PointerEventTypes.POINTERPICK, pointerInfo.event, pick),
                                PointerEventTypes.POINTERPICK,
                            );
                        } finally {
                            // Observable reuses EventState during nested notify;
                            // restore ownership of the outer double-tap event.
                            eventState.skipNextObservers = true;
                        }
                        return;
                    }
                }

                viewer.resetCamera();
            },
            -1,       // mask: all pointer types
            true,     // insertFirst — run before FramingBehavior's observer
        );

        this.selectionOutline = new SelectionOutline(babylonScene, this.traceScene);
        this.xrManager = new XRManager(babylonScene, this.traceScene, this);
        this.xrManager.setClickToSelect(this.clickToSelect);
        this.xrManager.setLocalizers(this._localizers, this._selectedPlacementId);
        if (this._imageAlignmentConfig) this.xrManager.setImageAlignment(this._imageAlignmentConfig.imageUrl, this._imageAlignmentConfig.widthM);
        if (config.xr) {
            this.xrManager.enableUI();
        }
        this.modelInfo = new ModelInfo(this.traceScene);

        // Now that clickToSelect and selectionOutline exist, apply the buffered preference
        // (the preference itself was already stored on traceScene above).
        if (this._pendingEditModeClickToSelect !== null) {
            this.clickToSelect.enableClickToSelect(this._pendingEditModeClickToSelect, this._clickToSelectOptions);
            if (this._pendingEditModeClickToSelect) {
                this.selectionOutline.enable();
            }
            this._pendingEditModeClickToSelect = null;
        }

        // If there's a src attribute, load it with progress tracking
        if (config.src) {
            // Create loading screen for initial load
            const loadingScreen = new CustomLoadingScreen("Initializing...", this);
            this.engine!.loadingScreen = loadingScreen;
            this.engine?.displayLoadingUI();

            Logger.log("Loading scene from src");

            // Load from URL with progress tracking.
            // Behaviors from HTML attributes are passed in so they're included
            // in the scene root's initial construction.
            await this.traceScene.loadFromURL(config.src, loadingScreen, config.behaviors);

            this.engine?.hideLoadingUI();
        } else {
            // No src - just start empty scene
            await this.traceScene.startScene();
        }
        throwIfDisposed(this);

        // Frame and activate rendering now that loading is done
        this.traceScene.frameScene();
        this.traceScene.startRendering();

        Logger.log("Scene is ready, calling load event");

        // Mark as ready and resolve any waiting promises
        this._ready = true;
        if (this._readyResolve) {
            this._readyResolve();
            this._readyResolve = null;
        }

        this.dispatchEvent(new Event("load"));

        // Ensure engine has correct size after initial load
        this.engine?.resize();
    }

    private initializeConfig(): ConfigData {
        // Handle logger attribute: "enabled" = DEBUG (everything), "log" = milestones
        // (LOG+WARN+ERROR, no per-object debug), "warn" = WARN+ERROR, default = ERROR only
        const loggerAttr = this.getAttribute("logger");
        if (loggerAttr === "enabled") {
            Logger.level = LogLevel.DEBUG;
        } else if (loggerAttr === "log") {
            Logger.level = LogLevel.LOG;
        } else if (loggerAttr === "warn") {
            Logger.level = LogLevel.WARN;
        } else if (loggerAttr === "none") {
            Logger.level = LogLevel.NONE;
        } else {
            Logger.level = LogLevel.ERROR; // Default: errors always surface
        }
        // Parse behaviors attribute if present
        let behaviors: BehaviorData[] | undefined;
        const behaviorsAttr = this.getAttribute("behaviors");
        if (behaviorsAttr) {
            try {
                behaviors = JSON.parse(behaviorsAttr) as BehaviorData[];
            } catch (e) {
                Logger.error(`[TraceViewer] Invalid behaviors attribute JSON: ${String(e)}`);
            }
        }

        return {
            src: this.getAttribute("src"),
            color: this.getAttribute("backgroundcolor"),
            prewarm: Number(this.getAttribute("prewarmParticles")) || 0,
            cameraRadius: Number(this.getAttribute("cameraradius")) || 1,
            alpha: this.getAttribute("alpha") ? Number(this.getAttribute("alpha")) : null,
            blobGLB: this.getAttribute("blobGLB"),
            environmentImage: this.getAttribute("environment-image") ?? undefined,
            behaviors,
            groundShadow: this.getAttribute("ground-shadow") !== "false",
            groundHeight: parseFloat(this.getAttribute("ground-height") ?? "") || undefined,
            xr: this.hasAttribute("xr"),
            playMode: this.getAttribute("play-mode") !== "false",
        };
    }

    disconnectedCallback() {
        Logger.log("TraceViewer element removed, disposing scene");
        this.dispose();
    }

    /**
     * Explicitly dispose of the viewer and release all resources.
     * This method is idempotent - safe to call multiple times.
     *
     * Called automatically when the element is removed from the DOM, and can
     * be called directly for offscreen viewers. Disposal is final: a disposed
     * viewer is not initialised by connecting it, whether or not it booted
     * before. Loads still running, and `whenReady()` now or later, reject with
     * an `AbortError`.
     */
    public dispose(): void {
        // First, so a throw during teardown cannot leave whenReady() pending,
        // and so a viewer disposed before it ever booted settles it too.
        this._failReady(disposedError());

        // Guard against multiple dispose calls
        if (!this.engine && !this.traceScene && !this._resizeHandler) {
            // Never booted: nothing to release, but connecting it later must not boot it.
            this.loaded = true;
            return;
        }

        Logger.log("TraceViewer disposing resources");
        this._ready = false;

        this.disposeScene();
        this.disposeEngine();

        // Remove resize listener
        if (this._resizeHandler) {
            window.removeEventListener("resize", this._resizeHandler);
            this._resizeHandler = null;
        }

        // Clear feature modules
        this.clickToSelect?.dispose();
        this.clickToSelect = null;
        this.selectionOutline = null;
        this.xrManager?.dispose();
        this.xrManager = null;
        this.modelInfo = null;
    }

    private disposeScene() {
        if (this.traceScene) {
            this.traceScene.dispose();
            this.traceScene = null;
        }
    }

    private disposeEngine() {
        if (this.engine) {
            this.engine.dispose();
            this.engine = null;
        }
    }

    // ============================
    // Click-to-Select functionality
    // ============================

    /**
     * Set the consumer's preferred click-to-select state for edit mode and apply it.
     * Play mode temporarily overrides this to `true`; exiting play mode
     * restores this preference automatically.
     *
     * May be called before the scene is loaded — the preference is buffered
     * and applied once TraceScene is created.
     * Supplied options replace the host's stored picking options; omitting
     * them keeps those options across disable/re-enable and scene reloads.
     */
    public enableClickToSelect(enabled: boolean, options?: ClickToSelectOptions): void {
        if (options) this._clickToSelectOptions = { ...options };
        if (this.traceScene) {
            this.traceScene.setEditModeClickToSelect(enabled);
        } else {
            this._pendingEditModeClickToSelect = enabled;
        }
        this.clickToSelect?.enableClickToSelect(enabled, this._clickToSelectOptions);
        if (enabled) {
            this.selectionOutline?.enable();
        }
    }

    /** Read the next opt-in edit-mode pick without selecting or advancing. */
    public peekNextPick(x?: number, y?: number): SceneObject | null {
        return this.clickToSelect?.peekNextPick(x, y) ?? null;
    }

    /** Suppress click selection after a host gizmo actually changes a transform. */
    public notifyClickToSelectDrag(): void {
        this.clickToSelect?.notifyDragOccurred();
    }

    /**
     * Apply click routing without updating the stored preference or the outline.
     * Outline is managed by enableClickToSelect() (consumer API) only — play mode
     * transitions should not permanently enable the outline layer.
     * @internal Called by TraceScene via SceneEventSystem for mode transitions.
     */
    public applyClickRouting(enabled: boolean): void {
        this.clickToSelect?.enableClickToSelect(enabled);
    }

    /**
     * Select an object (or deselect if null).
     * Updates selection state and emits `trace-viewer:selection-changed` event.
     * The visual outline updates automatically via the event.
     *
     * @param sceneObject - SceneObject to select, or null to deselect
     */
    public selectObject(sceneObject: SceneObject | null): void {
        this.traceScene?.select(sceneObject?.getPath() ?? null);
    }

    /**
     * Outline an explicit set of objects (editor multi-selection), overriding
     * the single-primary outline. Pass `null` to release control and fall back
     * to following the single primary selection.
     *
     * Editor-only; the runtime never calls this.
     */
    public setOutlinedSelectionSet(objects: ReadonlySet<SceneObject> | null): void {
        this.selectionOutline?.setOutlinedSet(objects);
    }

    /**
     * Set play mode state.
     * - Play mode (true): Behaviors are active, pointer events trigger actions
     * - Edit mode (false): Selection is active, pointer events select objects
     *
     * When entering play mode, 'start' interactions are fired on all objects.
     * Default is play mode (true).
     *
     * @param enabled - true for play mode (interactions), false for edit mode (selection)
     */
    public setPlayMode(enabled: boolean): void {
        this.traceScene?.setPlayMode(enabled);
        // Outline clears/restores automatically via the selection-changed event
        // (setPlayMode calls select(null) internally when entering play mode)
    }

    /**
     * Check if play mode is active.
     * @returns true if in play mode (interactions active), false if in edit mode (selection)
     */
    public isPlayMode(): boolean {
        return this.traceScene?.isPlayMode() ?? true;
    }

    /**
     * Set viewport safe area insets (CSS pixels from each canvas edge). Editor
     * panels or other overlapping UI should call this so behaviors like AutoUI
     * can offset their positioning, and so camera framing (`frameScene`,
     * `resetCamera`, focus) fits the content into the uncovered area and
     * centres it there. Framing reads the insets when it runs: a change moves
     * nothing until the next framing.
     */
    public setSafeAreaInsets(insets: Partial<SafeAreaInsets>): void {
        this.traceScene?.setSafeAreaInsets(insets);
    }

    /**
     * Get the current safe area insets.
     */
    public get safeAreaInsets(): Readonly<SafeAreaInsets> {
        return this.traceScene?.safeAreaInsets ?? { left: 0, top: 0, right: 0, bottom: 0 };
    }

    /**
     * Toggle debug visualization for all colliders in the scene.
     * Useful for debugging collider placement and size.
     * @param visible - true to show colliders as wireframe shapes, false to hide
     */
    public setCollidersDebugVisible(visible: boolean): void {
        this.traceScene?.setCollidersDebugVisible(visible);
    }

    // ============================
    // Ground Shadow
    // ============================

    /**
     * Enable or disable the shadow-receiving ground plane at y=0.
     * Can also be set declaratively via the `ground-shadow` HTML attribute.
     */
    public set groundShadow(enabled: boolean) {
        this.traceScene?.setGroundShadowEnabled(enabled);
    }

    public get groundShadow(): boolean {
        return this.traceScene?.groundShadowEnabled ?? false;
    }

    /**
     * Update the shadow ground plane height (scene-local Y, in meters).
     * Can also be set declaratively via the `ground-height` HTML attribute.
     */
    public set groundHeight(y: number) {
        this.traceScene?.setGroundHeight(y);
    }

    // ============================
    // Model Information Methods
    // ============================

    /** Get detailed information about a GLB model */
    public getModelInfo(sceneObject: SceneObject): GLBModelInfo | null {
        if (this.modelInfo) {
            return this.modelInfo.getModelInfo(sceneObject.getPath());
        }
        return null;
    }

    /** Get scene information - returns different data based on file type (GLB vs trace) */
    public getSceneInfo(): SceneInfoResult | null {
        if (this.modelInfo) {
            return this.modelInfo.getSceneInfo();
        }
        return null;
    }

    // ============================
    // XR (Augmented Reality)
    // ============================

    /**
     * Check if AR is supported on this device/browser.
     * Note: this is synchronous and may return false if the async support check
     * hasn't completed yet. Use {@link checkXRSupport} to await the result.
     * @returns true if immersive-ar sessions are supported
     */
    public get isXRSupported(): boolean {
        return this.xrManager?.isSupported ?? false;
    }

    /**
     * Await the XR support check. Unlike {@link isXRSupported}, this waits for
     * the async navigator.xr.isSessionSupported() call to complete.
     * @returns true if immersive-ar sessions are supported
     */
    public async checkXRSupport(): Promise<boolean> {
        return this.xrManager?.checkSupport() ?? Promise.resolve(false);
    }

    /**
     * Get the current XR session state.
     */
    public get xrState(): XRSessionState {
        return this.xrManager?.state ?? XRSessionState.NOT_SUPPORTED;
    }

    /**
     * SceneEventSystem capability: true when an immersive XR session is active.
     */
    public isXRActive(): boolean {
        return this.xrManager?.isActive ?? false;
    }

    /**
     * Enter AR mode. Must be called from a user gesture (click/tap).
     * The scene will be placed at the user's position.
     * @throws If AR is not supported or session fails to start
     */
    public async enterXR(): Promise<void> {
        if (!this.xrManager) {
            throw new Error("XR manager not initialized");
        }
        await this.xrManager.enterXR();
    }

    /**
     * Exit AR mode and return to normal 3D viewing.
     */
    public async exitXR(): Promise<void> {
        if (!this.xrManager) return;
        await this.xrManager.exitXR();
    }

    /**
     * Configure image-target alignment for AR mode.
     * When set, entering XR will show a headlocked image preview for
     * the user to align with the physical image before displaying content.
     * @param imageUrl - Full URL to the target image
     * @param imageWidthMeters - Physical width of the image in meters
     */
    public setImageAlignment(imageUrl: string, imageWidthMeters: number): void {
        this._imageAlignmentConfig = { imageUrl, widthM: imageWidthMeters };
        this.xrManager?.setImageAlignment(imageUrl, imageWidthMeters);
    }

    /** Configure authored placements plus one session choice. Null uses legacy alignment.
     * Throws for an invalid/ambiguous selection, without falling back to the primary.
     * Resolve URLs/heuristic poses with trackedLocalizersFromPlacements first.
     */
    public setLocalizers(localizers: readonly ScenePlacement[] | null, selectedPlacementId?: string): ScenePlacement | null {
        const selected = localizers === null ? null : requireLocalizerSession(localizers, selectedPlacementId).selected;
        this.xrManager?.setLocalizers(localizers, selectedPlacementId);
        this._localizers = localizers === null ? null : structuredClone([...localizers]);
        this._selectedPlacementId = selected?.id;
        return selected;
    }

    /** Explicit WebXR switch/restart, including choosing the same marker again. */
    public async selectLocalizer(placementId: string): Promise<void> {
        if (!this._localizers) throw new Error('No authored placements are configured.');
        requireLocalizerSession(this._localizers, placementId);
        const active = this.xrManager?.state === XRSessionState.ACTIVE;
        if (active) await this.xrManager!.exitXR();
        this.setLocalizers(this._localizers, placementId);
        if (active) await this.xrManager!.enterXR();
    }

    /**
     * Get a SceneObject by path.
     * @param path - Fully qualified transform path (e.g., "/Scene/UI/Panel") or bare name for legacy lookup.
     *               If omitted or null, returns the scene root object.
     * @returns The SceneObject instance, or null if not found
     *
     * @example
     * ```typescript
     * // Get scene root
     * const root = traceViewer.getSceneObject();
     *
     * // Get by absolute path (preferred)
     * const panel = traceViewer.getSceneObject("/Scene/UI/Panel");
     *
     * // Get by bare name (legacy, may fail with duplicate names)
     * const obj = traceViewer.getSceneObject("MyObject");
     *
     * // Iterate all scene objects
     * for (const obj of traceViewer.traceScene?.getSceneObjects() ?? []) {
     *     console.log(obj.getPath(), obj.getBehaviors());
     * }
     * ```
     */
    public getSceneObject(path?: string | null): SceneObject | null {
        if (!this.traceScene) {
            return null;
        }

        // If no path provided, return the scene root
        if (path === undefined || path === null) {
            return this.traceScene.getSceneRoot();
        }

        // Use resolveSceneObject for path-based lookup (supports FQTP and legacy bare names)
        return this.traceScene.resolveSceneObject(path);
    }
}

// Guarded: under Vite HMR this module can re-execute in the same page, and
// customElements.define throws on a duplicate name — which would fail the
// whole hot update. The first-registered class stays live; a refresh picks
// up new TraceViewer code.
if (!customElements.get("trace-viewer")) {
    customElements.define("trace-viewer", TraceViewer);
}

// Export type for TypeScript consumers
export type TraceViewerElement = TraceViewer;

// Export types for editor consumption (public API)
export type { SceneObjectData as SceneData } from "../scenedata/sceneobjectdata";

// Export behavior types and classes for new typed API
export { SceneObject } from "./SceneObject";
export { TraceBehavior } from "../behaviors/tracebehavior";
export { Frame } from "../behaviors/frame";
export { Model } from "../behaviors/model";
export { Animation } from "../behaviors/animation";
export { AnimationSequence } from "../behaviors/animationsequence";
export { Renderer } from "../behaviors/renderer";
export { Image } from "../behaviors/image";
export { Video } from "../behaviors/video";

// XR types
export { XRSessionState } from "../managers/XRManager";

// Export data types for behavior data manipulation
export type { BehaviorData, ValidationError, CommitResult, LoadError } from "../scenedata/behaviordata";
export type { FrameData } from "../scenedata/behaviors/framedata";
export type { AnimationSequenceData, AnimationStateConfig, AnimationSequenceStates } from "../scenedata/behaviors/animationsequencedata";
export type { RendererData, MaterialData } from "../scenedata/behaviors/rendererdata";
export type { VideoData } from "../scenedata/behaviors/videodata";
export type { ModelData } from "../scenedata/behaviors/modeldata";
export type { AnimationData } from "../scenedata/behaviors/animationdata";
// Note: LoadResult is already exported via the interface declaration above

declare global {
    interface HTMLElementTagNameMap {
        'trace-viewer': TraceViewer;
    }
}
